import React, { useState, useCallback, useEffect, useRef } from "react";
import { POSService } from "../../../services/pos.service";
import { SessionResponse } from "../types/pos.types";
import { POSContext, POSContextType } from "../context/POSContext";
import { useAuth } from "../../auth/hooks/useAuth";

export const POSProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const posService = useRef(new POSService()).current;
  const isCheckingSession = useRef(false);
  const sessionCheckAttempts = useRef(0);
  const MAX_SESSION_CHECK_ATTEMPTS = 3;
  const { user } = useAuth();

  const [sessionState, setSessionState] = useState<{
    id: string | null;
    data: SessionResponse | null;
    is_active: boolean;
    isLoading: boolean;
    error: Error | null;
  }>({
    id: localStorage.getItem("pos_session_id"),
    data: JSON.parse(localStorage.getItem("pos_session_data") || "null"),
    is_active: false,
    isLoading: true,
    error: null,
  });

  const updateSessionStorage = useCallback(
    (id: string | null, data: SessionResponse | null) => {
      if (id && data) {
        localStorage.setItem("pos_session_id", id);
        localStorage.setItem("pos_session_data", JSON.stringify(data));
      } else {
        localStorage.removeItem("pos_session_id");
        localStorage.removeItem("pos_session_data");
      }
    },
    []
  );

  const clearSession = useCallback(() => {
    updateSessionStorage(null, null);
    setSessionState((prev) => ({
      ...prev,
      id: null,
      data: null,
      is_active: false,
      isLoading: false,
      status: "none",
    }));
  }, [updateSessionStorage]);

  const checkSession = async () => {
    // Prevent concurrent session checks
    if (isCheckingSession.current) {
      console.warn(
        "Session check already in progress, ignoring duplicate call"
      );
      return;
    }

    // Limit check attempts to avoid infinite loops
    if (sessionCheckAttempts.current >= MAX_SESSION_CHECK_ATTEMPTS) {
      const error = new Error(
        `Maximum session check attempts (${MAX_SESSION_CHECK_ATTEMPTS}) reached`
      );
      console.error(error);
      setSessionState((prev) => ({
        ...prev,
        error,
        isLoading: false,
      }));
      return;
    }

    sessionCheckAttempts.current += 1;
    isCheckingSession.current = true;

    try {
      setSessionState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Ensure we have a user before checking session
      if (!user) {
        throw new Error("User authentication required");
      }

      const response = await posService.getActiveSession();

      // If no active session exists, clear the session state
      if (!response || !response.data) {
        clearSession();
        return;
      }

      const sessionData = response.data;
      const is_active = sessionData.status === "active";

      // Verify the session belongs to current user
      if (is_active && sessionData.id) {
        const sessionCashierId =
          typeof sessionData.cashier === "object"
            ? sessionData.cashier.id
            : sessionData.cashier;

        if (sessionCashierId !== user.id && sessionCashierId !== user._id) {
          console.warn("Session belongs to a different cashier");
          clearSession();
          throw new Error("This session belongs to another cashier");
        }

        // Session is valid and belongs to current user
        updateSessionStorage(sessionData.id, sessionData);
        setSessionState({
          id: sessionData.id,
          data: sessionData,
          is_active: true,
          isLoading: false,
          error: null,
        });

        // Reset attempt counter on success
        sessionCheckAttempts.current = 0;
      } else {
        clearSession();
      }
    } catch (error) {
      console.error("Session check failed:", error);
      clearSession();
      setSessionState((prev) => ({
        ...prev,
        error:
          error instanceof Error ? error : new Error("Session check failed"),
        isLoading: false,
      }));
      throw error; // Re-throw to allow components to handle it
    } finally {
      isCheckingSession.current = false;
      setSessionState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const startSession = useCallback(
    async (data: {
      initial_amount: number;
      denominations: {
        bills: Array<{ value: number; count: number }>;
        coins: Array<{ value: number; count: number }>;
      };
    }) => {
      // Prevent concurrent operations
      if (isCheckingSession.current) {
        console.warn(
          "Session operation already in progress, ignoring duplicate call"
        );
        return;
      }

      isCheckingSession.current = true;

      try {
        setSessionState((prev) => ({ ...prev, isLoading: true, error: null }));

        // Ensure we have a user before starting session
        if (!user) {
          throw new Error("User authentication required");
        }

        // Include user ID in session data
        const sessionPayload = {
          ...data,
          cashier: user.id || user._id,
          cashier_name: `${user.first_name} ${user.last_name}`,
        };

        const response = await posService.startSession(sessionPayload);
        const sessionData = response.data;

        if (sessionData?.id) {
          updateSessionStorage(sessionData.id, sessionData);
          setSessionState({
            id: sessionData.id,
            data: sessionData,
            is_active: true,
            isLoading: false,
            error: null,
          });

          // Reset attempt counter on success
          sessionCheckAttempts.current = 0;
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {
        console.error("Failed to start session:", error);
        setSessionState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error
              : new Error("Failed to start session"),
          isLoading: false,
        }));
        throw error; // Re-throw to allow components to handle it
      } finally {
        isCheckingSession.current = false;
        setSessionState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [updateSessionStorage, user]
  );

  const endSession = useCallback(
    async (data: {
      session_id: string;
      final_denominations: {
        bills: Array<{ value: number; count: number }>;
        coins: Array<{ value: number; count: number }>;
      };
    }) => {
      if (!sessionState.id) {
        console.warn("Cannot end session: No active session ID");
        return;
      }

      if (isCheckingSession.current) {
        console.warn(
          "Session operation already in progress, ignoring duplicate call"
        );
        return;
      }

      isCheckingSession.current = true;

      try {
        setSessionState((prev) => ({ ...prev, isLoading: true, error: null }));

        // Add cashier information
        const endSessionPayload = {
          ...data,
          cashier: user?.id || user?._id,
        };

        await posService.endSession(endSessionPayload);
        clearSession();
      } catch (error) {
        console.error("Failed to end session:", error);
        setSessionState((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error : new Error("Failed to end session"),
          isLoading: false,
          status: "ended",
        }));
        throw error; // Re-throw to allow components to handle it
      } finally {
        isCheckingSession.current = false;
        setSessionState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [sessionState.id, clearSession, user]
  );

  // Check for active session only when user logs in
  useEffect(() => {
    let isMounted = true;

    const checkActiveSession = async () => {
      // Only check if we have a user and we haven't checked before
      if (isMounted && user && !sessionState.is_active) {
        try {
          const response = await posService.getActiveSession();

          // If no active session exists, clear the session state
          if (!response || !response.data) {
            if (isMounted) {
              clearSession();
            }
            return;
          }

          const sessionData = response.data;
          const is_active = sessionData.status === "active";

          // Verify the session belongs to current user
          if (is_active && sessionData.id) {
            const sessionCashierId =
              typeof sessionData.cashier === "object"
                ? sessionData.cashier.id
                : sessionData.cashier;

            if (sessionCashierId !== user.id && sessionCashierId !== user._id) {
              console.warn("Session belongs to a different cashier");
              if (isMounted) {
                clearSession();
              }
              return;
            }

            // Session is valid and belongs to current user
            if (isMounted) {
              updateSessionStorage(sessionData.id, sessionData);
              setSessionState({
                id: sessionData.id,
                data: sessionData,
                is_active: true,
                isLoading: false,
                error: null,
              });
            }
          } else {
            if (isMounted) {
              clearSession();
            }
          }
        } catch (err) {
          console.error("Active session check failed:", err);
          if (isMounted) {
            clearSession();
          }
        }
      } else if (isMounted && !user) {
        // No user, clear any session data
        clearSession();
      }
    };

    checkActiveSession();

    return () => {
      isMounted = false;
    };
  }, [user]); // Only depend on user changes

  const clearError = useCallback(() => {
    setSessionState((prev) => ({ ...prev, error: null }));
  }, []);

  const contextValue: POSContextType = {
    posService,
    sessionId: sessionState.id,
    isSessionActive: sessionState.is_active,
    sessionData: sessionState.data,
    isLoading: sessionState.isLoading,
    error: sessionState.error,
    startSession,
    endSession,
    checkSession,
    clearError,
  };

  return (
    <POSContext.Provider value={contextValue}>{children}</POSContext.Provider>
  );
};
